import React, { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";

//All Loading related components
import { makeStyles } from "tss-react/mui";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { ThemeProvider } from "@emotion/react";

import SourceOutlinedIcon from "@mui/icons-material/SourceOutlined";
import MediationRoundedIcon from "@mui/icons-material/MediationRounded";
import DataArrayOutlinedIcon from "@mui/icons-material/DataArrayOutlined";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";

import CommonLayout from "../../Common/Layout";

import { InfoCard } from "./info-card";
import { DetailCard } from "./detail-card";
import { DATA_SOURCE, ENTITY } from "../../../util/Data/DataModeling";
import TopDataSets from "./TopDataSets";
import OperationalDashboard from "../../../service/operationalDashboard";
import { OverviewCardContainer } from "./card-container";
import HelpContextIcon from "../../Common/HelpPanel/HelpContextIcon";
import { createTheme } from "./overview/theme";
import ShareWorkspace from "../ShareWorkspace";
import WorkspaceSettings from "../WorkspaceSettings";
import { LinksRow } from "./linksRow";

const theme = createTheme();

const useStyles = makeStyles()((theme) => ({
  container: {
    marginBottom: "32px",
    marginLeft: "0px",
    marginRight: "0px",
    display: "flex",
    padding: "22px 30px 30px 30px",
    borderTop: 0,
    width: "100%",
    flexDirection: "column",
    "& .MuiAlert-message .MuiList-root": {
      listStyle: "square",
    },
    "& .MuiTableContainer-root": {
      borderRadius: "10px",
    },
  },
  skeleton: {
    borderRadius: "5px",
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
    width: "80%",
    height: 24,
  },
  overviewGrid: {
    marginBottom: "20px",
    "& .simplebar-placeholder": {
      display: "math",
    },
  },
  m0: {
    margin: 0,
    "& p": {
      margin: 0,
    },
  },
  label: {
    fontSize: "0.775rem",
    fontWeight: "Bold",
  },
}));

const WorkspaceDetail = () => {
  const { classes } = useStyles();
  const { Workspace_id } = useParams();
  const workspace = useSelector((state) => state.workspace);
  const user = useSelector((state) => state.user);

  const { Name: workspaceName, DBName: database, accessRole, Owner_id, Owner, Message } = workspace.workspace;
  const schema = workspace.schema;
  const owner = useMemo(() => ({ User_id: Owner_id, UserName: Owner }), [Owner, Owner_id]);
  const workspaceMessage = Message?.replaceAll("&lt;", "<").replaceAll("&gt;", ">") || "";

  const [totalDataSource, setTotalDataSource] = useState(0);
  const [totalDataRow, setTotalDataRow] = useState(0);
  const [totalEntity, setTotalEntity] = useState(0);
  const [openSettings, setOpenSettings] = useState(false);
  const [openSharedWorkspace, setOpenSharedWorkspace] = useState(false);

  const [loading, setLoading] = useState(true);

  const { User_id } = user.currentUser;

  useEffect(() => {
    const getDataSourceMetrics = async () => {
      const operationalDashboard = new OperationalDashboard();
      const result = await operationalDashboard.getDataSourceMetrics(Workspace_id);

      if (result?.success) {
        setTotalDataSource(result.data.totalDataSource);
        setTotalDataRow(result.data.totalDataRow);
        setTotalEntity(result.data.totalEntity);
      } else {
        setTotalDataSource(0);
        setTotalDataRow(0);
        setTotalEntity(0);
      }

      setLoading(false);
    };

    getDataSourceMetrics();
  }, [Workspace_id]);

  const $role = useMemo(() => {
    if (User_id === owner?.User_id) return "Owner";
    else {
      if (accessRole === 1) return "Admin";
      if (accessRole === 2) return "Member";
      return "Visitor";
    }
  }, [User_id, owner, accessRole]);

  const breadcrumbArr = useMemo(
    () => [
      { link: "/my-workspaces", displayName: "Workspaces", active: false },
      { link: "#", displayName: workspaceName, active: true },
    ],
    [workspaceName]
  );

  return (
    <CommonLayout
      title={
        <>
          {workspaceName}
          <HelpContextIcon screen="Workspace" />
        </>
      }
      breadcrumbArr={breadcrumbArr}
    >
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.TiffanyBlue.main;
          },
          flexGrow: 1,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <ThemeProvider theme={theme}>
            <Box className={classes.container}>
              <Grid container spacing={5} direction="row" sx={{ mb: 3 }}>
                <Grid
                  size={{
                    xs: 12,
                    sm: 6,
                    lg: 3
                  }}>
                  <DetailCard
                    color="#0AB2B2"
                    setOpenSettings={setOpenSettings}
                    setOpenSharedWorkspace={setOpenSharedWorkspace}
                    renderIcon={() => <AssignmentIndOutlinedIcon />}
                    sx={{ height: "100%" }}
                    items={[
                      {
                        key: "WORKSPACE ROLE",
                        value: $role,
                      },
                      {
                        key: "DATABASE NAME",
                        value: database,
                      },
                      {
                        key: "DEFAULT SCHEMA",
                        value: schema,
                      },
                    ]}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 6,
                    lg: 3
                  }}>
                  <InfoCard
                    title={<Typography className={classes.label}>{`Total ${DATA_SOURCE.plural}`}</Typography>}
                    value={loading ? <Skeleton className={classes.skeleton} variant="rectangular" /> : totalDataSource}
                    color="#0AB2B2"
                    renderIcon={() => <SourceOutlinedIcon />}
                    sx={{ height: "100%" }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 6,
                    lg: 3
                  }}>
                  <InfoCard
                    title={<Typography className={classes.label}>{`Total ${ENTITY.plural}`}</Typography>}
                    value={loading ? <Skeleton className={classes.skeleton} variant="rectangular" /> : totalEntity}
                    color="#0AB2B2"
                    renderIcon={() => <MediationRoundedIcon />}
                    sx={{ height: "100%" }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 6,
                    lg: 3
                  }}>
                  <InfoCard
                    title={<Typography className={classes.label}>Total Data Rows</Typography>}
                    value={loading ? <Skeleton className={classes.skeleton} variant="rectangular" /> : totalDataRow}
                    color="#0AB2B2"
                    renderIcon={() => <DataArrayOutlinedIcon />}
                    sx={{ height: "100%" }}
                  />
                </Grid>
              </Grid>

              <LinksRow
                sx={{ p: 0, mb: 3 }}
                containerStyle={{ padding: "10px" }}
                setOpenSettings={setOpenSettings}
                setOpenSharedWorkspace={setOpenSharedWorkspace}
              />

              {workspaceMessage && (
                <Grid container spacing={5} direction="row" sx={{ mb: 3 }}>
                  <Grid size={12}>
                    <OverviewCardContainer sx={{ height: "100%" }}>
                      <div className={classes.m0} dangerouslySetInnerHTML={{ __html: workspaceMessage }} />
                    </OverviewCardContainer>
                  </Grid>
                </Grid>
              )}

              <Box className={classes.overviewGrid}>
                <TopDataSets />
              </Box>
            </Box>
          </ThemeProvider>
        </Box>
      </Box>
      {openSharedWorkspace && <ShareWorkspace setOpen={setOpenSharedWorkspace} />}
      {openSettings && <WorkspaceSettings setOpen={setOpenSettings} />}
    </CommonLayout>
  );
};

export default WorkspaceDetail;
